<template>
  <div class="w-full flex flex-col gap-4 pb-10">
    <VideoAnalysisBreakdown 
      v-if="normalizedBreakdownData?.videoAnalysis && advertisement.video_duration" 
      :advertisement="advertisement"
      :retention-series="normalizedBreakdownData?.videoAnalysis ?? []"
      :loading="loadingAdCycle"
    />
    <GenderAgeBreakdown
      :selected-metric="selectedBreakdownMetrics.genderAge"
      :data-groups="normalizedBreakdownData?.genderAge ?? []"
      :currency-region="dataSummary?.currency"
      :loading="loadingAdCycle || (loadingMetrics && loadingBreakdownKey === 'genderAge')"
      :is-data-public="advertisement?.isDataPublic"
      @changeMetric="handleMetricChange('genderAge', $event)"
    />
    <AudienceSegmentBreakdown
      v-if="getMetricsPlatform === 'meta'"
      :selected-metrics="selectedBreakdownMetrics.audienceSegment"
      :data-groups="normalizedBreakdownData?.audienceSegment ?? []"
      :currency-region="dataSummary?.currency"
      :loading="loadingAdCycle || (loadingMetrics && loadingBreakdownKey === 'audienceSegment')"
      :is-data-public="advertisement?.isDataPublic"
      @changeMetric="handleMetricChange('audienceSegment', $event)"
    />
    <PlatformBreakdown 
      v-if="getMetricsPlatform === 'meta'"
      :selected-metrics="selectedBreakdownMetrics.platform"
      :data-groups="normalizedBreakdownData?.platform ?? []"
      :placement-groups="normalizedBreakdownData?.placement ?? []"
      :currency-region="dataSummary?.currency"
      :loading="loadingAdCycle || (loadingMetrics && loadingBreakdownKey === 'platform')"
      :is-data-public="advertisement?.isDataPublic"
      @changeMetric="handleMetricChange('platform', $event)"
    />
    <CountryBreakdown 
      :selected-metric="selectedBreakdownMetrics.country"
      :data-groups="normalizedBreakdownData?.country ?? []"
      :currency-region="dataSummary?.currency"
      :loading="loadingAdCycle || (loadingMetrics && loadingBreakdownKey === 'country')"
      :is-data-public="advertisement?.isDataPublic"
      @changeMetric="handleMetricChange('country', $event)"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

// Breakdown Components
import VideoAnalysisBreakdown from './breakdowns/VideoAnalysisBreakdown.vue'
import GenderAgeBreakdown from './breakdowns/GenderAgeBreakdown.vue'
import AudienceSegmentBreakdown from './breakdowns/AudienceSegmentBreakdown.vue'
import PlatformBreakdown from './breakdowns/PlatformBreakdown.vue'
import CountryBreakdown from './breakdowns/CountryBreakdown.vue'

const PLATFORM_ICONS = {
  facebook: require('@/assets/images/facebook.svg'),
  instagram: require('@/assets/images/instagram.svg'),
  messenger: require('@/assets/images/fb-messenger.svg'),
  external: require('@/assets/images/audience-network.svg')
}

export default {
  name: 'CreativeBreakdownAnalytics',
  props: {
    advertisement: {
      type: Object,
      required: true
    },
    selectedBreakdownMetrics: {
      type: Object,
      required: true
    },
    dataSummary: {
      type: Object,
      default: () => {}
    },
    loadingMetrics: {
      type: Boolean,
      default: false
    },
    loadingAdCycle: {
      type: Boolean,
      default: false
    }
  },
  components: {
    VideoAnalysisBreakdown,
    GenderAgeBreakdown,
    AudienceSegmentBreakdown,
    PlatformBreakdown,
    CountryBreakdown
  },
  data () {
    return {
      loadingBreakdownKey: null
    }
  },
  computed: {
    ...mapGetters('MetricsModule', ['getMetricsPlatform']),
    normalizedBreakdownData () {
      const breakdownData = this.advertisement.breakdowns
      if (!breakdownData) return {}

      const normalizedData = {}

      // NORMALIZE VIDEO ANALYSIS
      const retentionSeries = []
      const retentionCurveData = breakdownData.video_play_curve_actions
      if (retentionCurveData?.length) {
        const duration = this.advertisement.video_duration
        for (let i = 0; i < retentionCurveData.length; i++) {
          // Blame Meta for this...
          let timestamp = i < 15 
            ? i 
            : i < 18 
              ? 15 + (i - 15) * 5 
              : i < 21 
                ? 30 + (i - 18) * 10
                : 60
          timestamp = Math.min(timestamp, duration)
          retentionSeries.push({
            x: timestamp,
            y: Math.min(retentionCurveData[i], retentionCurveData?.[i - 1] ?? 100) // Correct anomalies in Meta's data
          })
        }
        normalizedData.videoAnalysis = retentionSeries
      }
      
      // NORMALIZE GENDER AGE
      const genderMetricKey = this.selectedBreakdownMetrics.genderAge
      const genderAgeData = { '18-24': [0, 0], '25-34': [0, 0], '35-44': [0, 0], '45-54': [0, 0], '55-64': [0, 0], '65+': [0, 0] }
      breakdownData.age_gender.forEach(item => {
        if (!genderAgeData[item.age]) return
        if (item.gender === 'male') genderAgeData[item.age][0] = item?.[genderMetricKey] ?? 0
        if (item.gender === 'female') genderAgeData[item.age][1] = item?.[genderMetricKey] ?? 0
      })
      normalizedData.genderAge = Object.entries(genderAgeData).map(([age, values]) => ({
        label: { text: age, icon: null },
        values
      }))

      // NORMALIZE AUDIENCE SEGMENT
      const audienceSegmentMetricKey = this.selectedBreakdownMetrics.audienceSegment
      const audienceSegmentData = { 
        'prospecting': { label: { text: 'New Audience', icon: null }, values: [0, 0] },
        'engaged': { label: { text: 'Engaged Audience', icon: null }, values: [0, 0] },
        'existing': { label: { text: 'Existing Customer', icon: null }, values: [0, 0] }
      }
      breakdownData.user_segment.forEach(item => {
        if (!audienceSegmentData[item.user_segment_key]) return
        const {left: leftMetric, right: rightMetric } = audienceSegmentMetricKey
        const values = [item?.[leftMetric] ?? 0, item?.[rightMetric] ?? 0]
        audienceSegmentData[item.user_segment_key].values = values
      })
      normalizedData.audienceSegment = Object.values(audienceSegmentData)

      // Platform meta and key mapping for platform and placement breakdowns
      const platformMeta = {
        'facebook': { text: 'Facebook', icon: PLATFORM_ICONS.facebook },
        'instagram': { text: 'Instagram', icon: PLATFORM_ICONS.instagram },
        'messenger': { text: 'Messenger', icon: PLATFORM_ICONS.messenger },
        'network': { text: 'Network', icon: PLATFORM_ICONS.external }
      }
      // For mapping keys from API to meta info
      const platformMap = new Map([['facebook', 'facebook'], ['instagram', 'instagram'], ['threads', 'instagram'], ['messenger', 'messenger'], ['audience_network', 'network']])

      // NORMALIZE PLATFORMS
      const platformMetricKey = this.selectedBreakdownMetrics.platform
      const platformData = Object.fromEntries(Object.entries(platformMeta).map(([key, value]) => [key, { label: value, values: [0, 0] }]))
      breakdownData.publisher_platform.forEach(item => {
        if (!platformMap.has(item.publisher_platform)) return
        const platformMetaKey = platformMap.get(item.publisher_platform)
        const {left: leftMetric, right: rightMetric } = platformMetricKey
        const values = [item?.[leftMetric] ?? 0, item?.[rightMetric] ?? 0]
        // Sum with the existing values
        platformData[platformMetaKey].values = [
          platformData[platformMetaKey].values[0] + values[0],
          platformData[platformMetaKey].values[1] + values[1]
        ]
      })
      normalizedData.platform = Object.values(platformData)

      // NORMALIZE PLACEMENT
      const placementMetricKey = this.selectedBreakdownMetrics.platform // Use same metrics as platform
      const placementData = breakdownData.publisher_platform_position.map(item => {
        const platformMetaKey = platformMap.get(item.publisher_platform)
        const {left: leftMetric, right: rightMetric } = placementMetricKey
        const values = [item?.[leftMetric] ?? 0, item?.[rightMetric] ?? 0]
        return {
          label: { text: snakeToTitle(item.platform_position), icon: platformMeta?.[platformMetaKey]?.icon ?? null },
          values
        }
      })
      normalizedData.placement = placementData
        .filter(item => item.values[0] > 0 || item.values[1] > 0) // Remove placements with no values
        .sort((a, b) => b.values[0] - a.values[0]) // Sort by left metric

      // NORMALIZE COUNTRY
      const countryMetricKey = this.selectedBreakdownMetrics.country
      const countryData = breakdownData.country.map(item => ({
        country: item.country,
        value: item?.[countryMetricKey] ?? 0
      }))
      countryData.sort((a, b) => b.value - a.value)
      normalizedData.country = countryData

      return normalizedData
    }
  },
  methods: {
    handleMetricChange (breakdownKey, value) {
      this.loadingBreakdownKey = breakdownKey
      this.$emit('change-metric', { breakdownKey, value })
    }
  }
}

const snakeToTitle = (str) => str.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())
</script>

<style scoped>
.breakdown-card {
  width: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  border: 1px solid #EAEFF3;
}
</style>